import React, { useEffect, useState } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LearnPage from './LearnPage';
import AlphabetTables from '../../components/AlphabetTables';
import { useCatalog } from '../../hooks/useCatalog';
import { useLangMap } from '../../hooks/useLanguages';
import { fetchData } from '../../services/dataApi';
import { useAuth } from '../../context/AuthContext';
import { buildBasicSections } from '../../services/basicLearning';

function useLearnData(targetCode, nativeCode) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!targetCode || !nativeCode) return;
    let active = true;
    setLoading(true);
    setError(null);
    const fetchNativeItems = async () => {
      const englishItems = await fetchData('basic-phrases-sentences/en/basic.json');
      if (nativeCode === 'en') return englishItems;
      const localizedItems = await fetchData(
        `basic-phrases-sentences/${nativeCode}/basic.json`,
      ).catch(() => []);
      const localizedByKey = new Map(localizedItems.map((item) => [item.key, item]));
      return englishItems.map((item) => ({
        ...item,
        ...(localizedByKey.get(item.key) || {}),
        text: localizedByKey.get(item.key)?.text || item.text,
      }));
    };
    const fetchPronunciations = async () => {
      const load = async (code) => {
        try {
          const pronunciations = await fetchData(
            `basic-phrases-sentences/${targetCode}/pronunciation/${code}.json`,
          );
          return pronunciations && typeof pronunciations === 'object'
            ? Object.fromEntries(
              Object.entries(pronunciations).filter(([, value]) => String(value || '').trim()),
            )
            : {};
        } catch {
          return {};
        }
      };

      const englishPronunciations = await load('en');
      if (nativeCode === 'en') return englishPronunciations;
      return { ...englishPronunciations, ...await load(nativeCode) };
    };

    Promise.all([
      fetchData(`basic-phrases-sentences/${targetCode}/basic.json`),
      fetchNativeItems(),
      fetchPronunciations(),
    ])
      .then(([targetItems, nativeItems, pronunciations]) => {
        if (!active) return;
        setData(buildBasicSections(targetItems, nativeItems, pronunciations));
        setLoading(false);
      })
      .catch((e) => {
        if (!active) return;
        setError(e.message);
        setLoading(false);
      });
    return () => { active = false; };
  }, [targetCode, nativeCode]);

  return { data, loading, error };
}

function useAlphabetData(langCode, nativeCode, enabled) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!enabled || !langCode) {
      setData(null);
      return;
    }
    let active = true;
    setData(null);
    Promise.all([
      fetchData(`alphabet/${langCode}/alphabet.json`),
      nativeCode && nativeCode !== langCode
        ? fetchData(`alphabet/${langCode}/pronunciation/${nativeCode}.json`).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([alphabet, overlay]) => {
        if (!active) return;
        setData({
          ...alphabet,
          localizedIntroduction: overlay?.introduction || null,
        });
      })
      .catch(() => {
        if (active) setData(null);
      });
    return () => { active = false; };
  }, [langCode, nativeCode, enabled]);

  return data;
}

const LATIN_SCRIPT = new Set(['en', 'de', 'fr', 'es', 'nl', 'it', 'pt', 'pl', 'sv', 'no', 'da', 'fi', 'cs', 'hu', 'ro', 'tr', 'vi', 'id', 'ms', 'fil', 'sw']);
const CYRILLIC_SCRIPT = new Set(['ru']);
const INDIC_SCRIPT = new Set(['hi', 'mr']);
function sharesScript(a, b) {
  return [LATIN_SCRIPT, CYRILLIC_SCRIPT, INDIC_SCRIPT].some((group) => group.has(a) && group.has(b));
}

export default function GenericLearnPage() {
  const { langCode } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const { catalog, loading: catLoading } = useCatalog();
  const { langMap } = useLangMap();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'alphabet' ? 'alphabet' : 'basics');

  const catalogEntry = catalog?.[langCode];
  const hasBasics   = catalogEntry?.features?.includes('basics');
  const hasAlphabet = catalogEntry?.features?.includes('alphabet');

  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const targetCode = langCode;
  const { data: learnData, loading: learnLoading, error: learnError } = useLearnData(targetCode, nativeCode);
  const alphabetData = useAlphabetData(langCode, nativeCode, hasAlphabet);

  useEffect(() => {
    if (!catLoading && catalog && !hasAlphabet && activeTab === 'alphabet') {
      setActiveTab('basics');
    }
  }, [catLoading, catalog, hasAlphabet, activeTab]);

  if (!catLoading && catalog && !catalogEntry) {
    return <Navigate to="/" replace />;
  }

  if (!catLoading && catalog && !hasBasics) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="page-shell">
      {hasAlphabet && (
        <div className="tabs">
          <button
            className={`tab-btn ${activeTab === 'basics' ? 'active' : ''}`}
            onClick={() => setActiveTab('basics')}
          >
            {t('learn.basicsTab')}
          </button>
          <button
            className={`tab-btn ${activeTab === 'alphabet' ? 'active' : ''}`}
            onClick={() => setActiveTab('alphabet')}
          >
            {t('learn.alphabetPronTab')}
          </button>
        </div>
      )}

      {activeTab === 'basics' && (
        learnLoading ? (
          <div className="page-panel">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-slate-100 rounded-xl" />
              ))}
            </div>
          </div>
        ) : learnError ? (
          <div className="page-panel">
            <p className="text-red-600">{t('learn.failed')}</p>
          </div>
        ) : learnData ? (
          <LearnPage
            title={langMap[targetCode]?.label || targetCode.toUpperCase()}
            sections={learnData}
            targetDir={langMap[targetCode]?.dir || 'ltr'}
            nativeDir={langMap[nativeCode]?.dir || 'ltr'}
            targetTts={langMap[targetCode]?.tts || targetCode}
          />
        ) : null
      )}

      {activeTab === 'alphabet' && (
        alphabetData ? (
          <AlphabetTables
            data={alphabetData}
            compact={sharesScript(targetCode, nativeCode)}
            nativeCode={nativeCode}
            targetCode={targetCode}
            nativeLabel={langMap[nativeCode]?.label}
            targetLabel={langMap[targetCode]?.label}
            nativeDir={langMap[nativeCode]?.dir || 'ltr'}
            targetDir={langMap[targetCode]?.dir || 'ltr'}
          />
        ) : (
          <div className="page-panel">
            <div className="animate-pulse space-y-4">
              {[1, 2].map((i) => <div key={i} className="h-48 bg-slate-100 rounded-xl" />)}
            </div>
          </div>
        )
      )}
    </div>
  );
}
